import React, {memo, useState} from 'react'
import {withStyles, Typography} from '@material-ui/core';
import PropTypes from 'prop-types';
import Form from 'components/Form';
import NipRow from './components/NipRow';
import TextInput from 'components/TextInput';
import Tr from 'translations/Translations';
import * as Yup from 'yup'
import validation from 'constants/ValidationShema'
import InputLimits from 'constants/InputLimits';
import PostCodeRow from './components/PostCodeRow';
import getObjectValue from "libs/getObjectValue";
import ContractorService from "services/ContractorService";
import Select, {createSelectItem} from "../../Select";
import {ContractorCompanySize, ContractorCompanySizeLabel} from "../../../constants/ContractorCompanySize";

const limits = InputLimits.procedure.contractor

const style = theme => ({
    nameExist: {
        fontSize: '0.75rem'
    }
})

const PL_PREFIX = "PL"

const translationsMap = {
    "vatin": "pesel_exist",
    "email": "email_exist",
    "postCode": "post_code_exist"
}

function InviteContactorForm(
    {
        classes,
        onSubmit,
        id,
        contractorList,
        defaultValues = {},
        collectPostCode,
        collectAddress,
        addressRequired = true,
        collectCity,
        cityRequired = true,
        collectRegon,
        collectCompanySize,
        collectIdentity,
        checkForExistingVatins,
        demandId
    }) {
    const handleSubmit = (value) => onSubmit(value);

    const [ctrMap, setCtrMap] = useState({});

    const onBlurNip = (e,formikProps) => {
        const number = getObjectValue('vatin.number', formikProps.values, '');
        const prefix = getObjectValue('vatin.prefix', formikProps.values, 'PL');
        const key = prefix + number;

        if(ctrMap[key] === undefined) {
            if(number && number.length >= limits.NIP_MIN && number.length <= limits.NIP_MAX) {
                ContractorService.check(demandId, prefix, number).then((resp) => {
                    if(resp.success) {
                        setCtrMap(x => ({...x, [key]: resp.result}))
                    }
                })
            }
        }
    }



    return (
        <Form
            className={classes.root}
            id={id}
            onSubmit={handleSubmit}
            initialValues={(() => {
                const initValues = {
                    name: getObjectValue('name', defaultValues, ''),
                    email: getObjectValue('email', defaultValues, ''),
                    vatin: {
                        number: getObjectValue('vatin.number', defaultValues, ''),
                        prefix: getObjectValue('vatin.prefix', defaultValues, 'PL'),
                    }
                };

                if (collectPostCode)
                    initValues.postCode = getObjectValue('postCode', defaultValues, '');

                if (collectAddress)
                    initValues.address = getObjectValue('address', defaultValues, '');

                if (collectCity)
                    initValues.city = getObjectValue('city', defaultValues, '');

                if (collectRegon)
                    initValues.regon = getObjectValue('regon', defaultValues, '');

                if (collectCompanySize)
                    initValues.companySize = getObjectValue('companySize', defaultValues, '');

                if(collectIdentity)
                    initValues.identity = getObjectValue('identity', defaultValues, '');

                return initValues;
            })()}
            validationSchema={Yup.lazy((value) => {
                let errorList = {}
                let nipAndPostCodeError = {}
                const numberLength = value.vatin?.number ? value.vatin.number.length : 0;
                if (contractorList?.items) {
                    contractorList.items.forEach(item => {
                        Object.keys(value).forEach(valueKey => {
                            if ((!collectIdentity || value.identity !== item.contractor.identity) && valueKey === 'vatin' && typeof value[valueKey] === 'object') {
                                Object.keys(value[valueKey]).forEach(innerItem => {
                                    if (innerItem === "number" &&
                                        value[valueKey][innerItem] === item.contractor[valueKey][innerItem].replace(/\s+/gm, '')) {
                                        if (numberLength === 11) errorList[valueKey] = valueKey
                                        else nipAndPostCodeError[valueKey] = valueKey
                                    }
                                })
                            }
                            if (translationsMap[valueKey] && value[valueKey] === item.contractor[valueKey]
                                && (!collectIdentity || value.identity !== item.contractor.identity)) {

                                if (valueKey === "postCode") nipAndPostCodeError[valueKey] = valueKey
                                else errorList[valueKey] = valueKey
                            }
                        });
                    });
                }

                const validateObj = {
                    name: validation.stringMax(limits.NAME),
                    email: validation.emailMax(limits.EMAIL)
                };

                if (collectAddress)
                    validateObj.address = addressRequired ? validation.stringMax(limits.ADDRESS) : validation.stringMaxNoRq(limits.ADDRESS);

                if (collectCity)
                    validateObj.city = cityRequired ? validation.stringMax(limits.CITY) : validation.stringMaxNoRq(limits.CITY);

                if (collectPostCode || value?.vatin?.number) {
                    validateObj.vatin = validation.object({
                        number: value.vatin.prefix === PL_PREFIX ?
                            validation.vatinOrPeselPL(Tr('invalid_vatin_for_country', {countryCode: value.vatin.prefix}))
                            : validation.stringMinAndMax(limits.NIP_MIN, limits.NIP_MAX)
                    });
                }

                if (collectPostCode) {
                    validateObj.postCode = value.vatin.prefix === PL_PREFIX ? validation.stringMin(limits.POSTCODE_PL) : validation.stringMax(limits.EMAIL);
                }

                if(collectRegon)
                    validateObj.regon = validation.stringMaxNoRq(limits.EMAIL);

                if(collectCompanySize)
                    validateObj.companySize = validation.string()

                let objShape = {}
                Object.keys(validateObj).forEach(item => {
                    if (errorList[item] === item) {
                        if (item === 'vatin') {
                            objShape[item] = validation.object({
                                number: validation.immediatelyError(Tr(translationsMap[item]))
                            })
                        } else objShape[item] = validation.immediatelyError(Tr(translationsMap[item]))
                    } else objShape[item] = validateObj[item]
                });
                if (Object.keys(nipAndPostCodeError).length === 2 && numberLength !== 11) {
                    objShape = {
                        ...objShape,
                        "vatin": validation.object({
                            number: validation.immediatelyError(Tr("repeat_nip_and_postCode_error"))
                        }),
                        "postCode": validation.immediatelyError(Tr("repeat_nip_and_postCode_error"))
                    }
                }

                return Yup.object().shape({
                    ...objShape
                })
            })}
        >
            {(formikProps) => {

                const number = getObjectValue('vatin.number', formikProps.values, '');
                const prefix = getObjectValue('vatin.prefix', formikProps.values, 'PL');
                const key = prefix + number;
                const fixedname = ctrMap[key];

                if(!!fixedname) {
                    const name = getObjectValue('name', formikProps.values, null);
                    if(fixedname !== name) {
                        formikProps.setFieldValue("name", fixedname)
                    }
                }

                return <>
                    <NipRow
                        formikProps={formikProps}
                        limit={limits.PESEL}
                        defaultValue={PL_PREFIX}
                        prefix={formikProps.values.vatin.prefix}
                        required={collectPostCode}
                        onBlur={checkForExistingVatins ? (e) => onBlurNip(e, formikProps) : undefined}
                    />
                    {!!fixedname && <Typography className={classes.nameExist} color="primary">{Tr('invitation_contractor_exist')}</Typography>}
                    <TextInput
                        id='name'
                        label={Tr('name')}
                        formikProps={formikProps}
                        required
                        limit={limits.NAME}
                        multiline
                        disabled={!!fixedname}
                    />

                    {collectAddress && <TextInput
                        id='address'
                        label={Tr('address')}
                        formikProps={formikProps}
                        limit={limits.ADDRESS}
                        required={addressRequired}
                    />}

                    {collectPostCode && <PostCodeRow
                        formikProps={formikProps}
                        limits={limits}
                        defaultValue={PL_PREFIX}
                        required
                        prefix={formikProps.values.vatin.prefix}
                    />}

                    {collectCity && <TextInput
                        id='city'
                        label={Tr('city')}
                        formikProps={formikProps}
                        limit={limits.CITY}
                        required={cityRequired}
                    />}

                    {collectRegon && <TextInput
                        id='regon'
                        label={Tr('regon')}
                        formikProps={formikProps}
                    />}

                    {collectCompanySize &&
                        <Select
                            id='companySize'
                            label={Tr('contractor_company_size')}
                            formikProps={formikProps}
                            items={Object.keys(ContractorCompanySize).map(x=> createSelectItem(x, ContractorCompanySizeLabel(x)))}
                            required
                        />
                    }

                    <TextInput
                        id='email'
                        label={Tr('email')}
                        formikProps={formikProps}
                        required
                        limit={limits.EMAIL}
                    />
                </>
            }}
        </Form>
    )
}

InviteContactorForm.propTypes = {
    classes: PropTypes.any,
}

export default withStyles(style)(memo(InviteContactorForm));